import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { PackageArt, Product } from '@/types'

import { useRef, useState } from 'react'
import Image from 'next/image'
import {
    Box,
    Hand,
    Pause,
    Play,
    RotateCcw,
    ZoomIn,
    ZoomOut
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { lighten, safeHex } from '@/lib/color'
import { createBagControl, useBagDrag } from '@/hooks/use-bag-drag'

import { useStage } from './stage-context'
import { ViewerView3D } from './three-loaders'
import { PackageFallback } from './package-fallback'

interface ProductGalleryProps {
    product: Product
    art: PackageArt
}

const ANGLES = [
    { label: 'Depan', value: 0 },
    { label: 'Samping', value: Math.PI / 2 },
    { label: 'Belakang', value: Math.PI }
]

export const ProductGallery: FC<ProductGalleryProps> = ({ product, art }): ReactNode => {
    const { status, reducedMotion } = useStage()
    const control = useRef(createBagControl({ rotationY: -0.35, autoRotate: !reducedMotion }))
    const drag = useBagDrag(control)
    const [selected, setSelected] = useState<number>(-1)
    const [ready, setReady] = useState(false)
    const [autoRotate, setAutoRotate] = useState(true)
    const [interacted, setInteracted] = useState(false)
    const photos = [product.image, ...product.gallery].filter((url): url is string => Boolean(url))
    const alts = [product.image_alt, ...product.gallery_alt]
    const use3d = status === 'loading' || status === 'ready'
    const showing3d = selected === -1
    const color = safeHex(product.package_color, '#F7B32B')

    const syncAuto = (value: boolean) => {
        control.current.autoRotate = value
        setAutoRotate(value)
    }

    const turnTo = (angle: number) => {
        const current = control.current.rotationY
        const turns = Math.round((current - angle) / (Math.PI * 2))

        control.current.rotationY = angle + turns * Math.PI * 2
        control.current.rotationX = 0
        control.current.velocity = 0
        syncAuto(false)
    }

    const zoom = (delta: number) => {
        control.current.zoom = Math.min(1.45, Math.max(0.8, control.current.zoom + delta))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') control.current.rotationY -= Math.PI / 8
        else if (event.key === 'ArrowRight') control.current.rotationY += Math.PI / 8
        else if (event.key === 'ArrowUp') control.current.rotationX = Math.max(-0.45, control.current.rotationX - 0.1)
        else if (event.key === 'ArrowDown') control.current.rotationX = Math.min(0.45, control.current.rotationX + 0.1)
        else if (event.key === '+' || event.key === '=') zoom(0.1)
        else if (event.key === '-') zoom(-0.1)
        else return

        event.preventDefault()
        syncAuto(false)
    }

    return (
        <div>
            <div
                className='relative aspect-square overflow-hidden rounded-[36px] border-2 border-brand-choco shadow-[0_10px_0_0_#3B1F0E]'
                style={{ background: `radial-gradient(circle at 50% 38%, ${lighten(color, 0.75)} 0%, ${lighten(color, 0.45)} 60%, ${lighten(color, 0.25)} 100%)` }}
            >
                <div className='bg-sunburst pointer-events-none absolute inset-0 opacity-70' aria-hidden='true' />

                {showing3d ? (
                    <div
                        {...drag}
                        onPointerDownCapture={() => {
                            setInteracted(true)
                            setAutoRotate(false)
                        }}
                        onKeyDown={onKeyDown}
                        tabIndex={0}
                        role='img'
                        aria-label={`Model 3D kemasan ${product.name}. Geser atau gunakan tombol panah untuk memutar.`}
                        className='absolute inset-0 cursor-grab touch-pan-y outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-brand-orange/50 active:cursor-grabbing'
                    >
                        <PackageFallback
                            art={art}
                            cmsId={product.id}
                            className={cn('absolute inset-0 transition-opacity duration-500', ready && status === 'ready' ? 'opacity-0' : 'opacity-100')}
                        />
                        {use3d && (
                            <ViewerView3D
                                art={art}
                                control={control}
                                reducedMotion={reducedMotion}
                                onReady={() => setReady(true)}
                                className='absolute inset-0'
                            />
                        )}
                    </div>
                ) : (
                    <Image
                        src={photos[selected]}
                        alt={alts[selected] || `Foto ${product.name}`}
                        fill
                        sizes='(min-width: 1024px) 560px, 100vw'
                        className='object-cover'
                        priority
                    />
                )}

                {showing3d && status === 'unsupported' && (
                    <p className='absolute inset-x-4 bottom-4 rounded-2xl bg-white/90 px-4 py-2 text-center text-xs font-medium text-brand-choco'>
                        Perangkatmu belum mendukung tampilan 3D, jadi kami tampilkan versi gambar.
                    </p>
                )}

                {showing3d && use3d && (
                    <>
                        {!interacted && (
                            <span className='pointer-events-none absolute left-1/2 top-4 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-brand-choco/85 px-3 py-1.5 text-xs font-semibold text-brand-cream'>
                                <Hand className='size-3.5' aria-hidden='true' />
                                Geser untuk memutar 360°
                            </span>
                        )}

                        <div className='absolute inset-x-3 bottom-3 flex flex-wrap items-center justify-between gap-2'>
                            <div className='flex gap-1 rounded-full border-2 border-brand-choco bg-white p-1 shadow-pop-sm' role='group' aria-label='Sudut pandang'>
                                {ANGLES.map(angle => (
                                    <button
                                        key={angle.label}
                                        type='button'
                                        onClick={() => turnTo(angle.value)}
                                        className='rounded-full px-3 py-1.5 font-heading text-xs font-semibold text-brand-choco transition-colors hover:bg-brand-yellow'
                                    >
                                        {angle.label}
                                    </button>
                                ))}
                            </div>
                            <div className='flex gap-1 rounded-full border-2 border-brand-choco bg-white p-1 shadow-pop-sm'>
                                <button
                                    type='button'
                                    onClick={() => syncAuto(!autoRotate)}
                                    className='inline-flex size-8 items-center justify-center rounded-full text-brand-choco hover:bg-brand-yellow'
                                    aria-label={autoRotate ? 'Hentikan putaran otomatis' : 'Putar otomatis 360 derajat'}
                                    aria-pressed={autoRotate}
                                >
                                    {autoRotate ? <Pause className='size-4' /> : <Play className='size-4' />}
                                </button>
                                <button type='button' onClick={() => zoom(0.12)} className='inline-flex size-8 items-center justify-center rounded-full text-brand-choco hover:bg-brand-yellow' aria-label='Perbesar'>
                                    <ZoomIn className='size-4' />
                                </button>
                                <button type='button' onClick={() => zoom(-0.12)} className='inline-flex size-8 items-center justify-center rounded-full text-brand-choco hover:bg-brand-yellow' aria-label='Perkecil'>
                                    <ZoomOut className='size-4' />
                                </button>
                                <button
                                    type='button'
                                    onClick={() => {
                                        control.current.zoom = 1
                                        turnTo(0)
                                    }}
                                    className='inline-flex size-8 items-center justify-center rounded-full text-brand-choco hover:bg-brand-yellow'
                                    aria-label='Atur ulang tampilan'
                                >
                                    <RotateCcw className='size-4' />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <ul className='mt-4 flex gap-3 overflow-x-auto pb-2 no-scrollbar' aria-label='Galeri produk'>
                <li>
                    <button
                        type='button'
                        onClick={() => setSelected(-1)}
                        className={cn(
                            'flex size-20 flex-col items-center justify-center gap-1 rounded-2xl border-2 font-heading text-xs font-semibold transition-all',
                            showing3d ? 'border-brand-choco bg-brand-yellow text-brand-choco shadow-pop-sm' : 'border-brand-choco/15 bg-white text-brand-choco hover:border-brand-choco/50'
                        )}
                        aria-pressed={showing3d}
                    >
                        <Box className='size-6' aria-hidden='true' />
                        Kemasan 3D
                    </button>
                </li>
                {photos.map((photo, index) => (
                    <li key={`${photo}-${index}`}>
                        <button
                            type='button'
                            onClick={() => setSelected(index)}
                            className={cn(
                                'relative block size-20 overflow-hidden rounded-2xl border-2 transition-all',
                                selected === index ? 'border-brand-choco shadow-pop-sm' : 'border-brand-choco/15 hover:border-brand-choco/50'
                            )}
                            aria-pressed={selected === index}
                            aria-label={`Lihat foto ${index + 1}`}
                        >
                            <Image src={photo} alt={alts[index] || `Foto ${product.name} ${index + 1}`} fill sizes='80px' className='object-cover' />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    )
}
